import { z } from 'zod'

/**
 * `GET /api/v1/admin/system` (platform admin): the deployment's non-secret
 * configuration for the in-app Docs page. Secrets are reported only as
 * present/absent booleans; their values never leave the Worker.
 */

/** Every Worker secret the portal reads (`wrangler secret put`). Names only. */
export const SYSTEM_SECRET_NAMES = [
  'SESSION_SECRET',
  'TOTP_ENC_KEY',
  'GOOGLE_CLIENT_ID',
  'GOOGLE_CLIENT_SECRET',
  'MS_CLIENT_ID',
  'MS_CLIENT_SECRET',
  'RESEND_API_KEY',
] as const

export type SystemSecretName = (typeof SYSTEM_SECRET_NAMES)[number]

export const DEPLOYMENT_MODES = ['demo', 'production'] as const
export type DeploymentMode = (typeof DEPLOYMENT_MODES)[number]

export const systemFeedProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  dataset: z.string(),
  simulated: z.boolean(),
  licence_status: z.string(),
  /** From `feed_providers.enabled`; null when the row is not there yet. */
  enabled: z.boolean().nullable(),
  /** "Hourly at :15" / "Daily at 05:00 UTC". */
  schedule: z.string(),
  late_after_minutes: z.number().int(),
})

export const systemCronSchema = z.object({
  /** Cron expression, UTC. */
  cron: z.string(),
  jobs: z.array(z.string()),
})

export const adminSystemSchema = z.object({
  generated_at: z.string(),
  mode: z.enum(DEPLOYMENT_MODES),
  /** Plain-language reasons this deployment counts as a demo. */
  mode_reasons: z.array(z.string()),
  version: z.string(),
  origins: z.object({ app: z.string(), marketing: z.string() }),
  /** `DEMO_CLOCK` when it pins the server clock, else null (real time). */
  demo_clock: z.string().nullable(),
  auth: z.object({
    privileged_mfa: z.enum(['required', 'optional']),
    admin_domain: z.string(),
    admin_extra_emails: z.array(z.string()),
    operator_emails: z.array(z.string()),
    /** Whether `FRUUIT_ENTRA_TID` is set (the id itself is not returned). */
    entra_tenant_configured: z.boolean(),
    providers: z.object({ google: z.boolean(), microsoft: z.boolean(), email: z.boolean() }),
    /** Local dev only: OTP codes and invite links are logged instead of emailed. */
    email_dev_log: z.boolean(),
  }),
  email: z.object({ from: z.string(), ops_alert_email: z.string() }),
  feeds: z.array(systemFeedProviderSchema),
  cron: z.array(systemCronSchema),
  bindings: z.object({ db: z.boolean(), kv: z.boolean(), files: z.boolean(), assets: z.boolean() }),
  secrets: z.array(z.object({ name: z.enum(SYSTEM_SECRET_NAMES), present: z.boolean() })),
})

export type AdminSystem = z.infer<typeof adminSystemSchema>
export type SystemFeedProvider = z.infer<typeof systemFeedProviderSchema>
